import React from 'react';
import { Link } from 'react-router-dom';
import { Workflow, Target, CheckCircle, FileText, BarChart3, ArrowRight } from 'lucide-react';

const WorkflowManagement = () => {
  const workflowFeatures = [
    {
      id: 'builder',
      title: 'Visual Workflow Builder',
      description: 'Create custom workflows with our intuitive drag-and-drop interface.',
      icon: <Target size={24} />,
      path: '/workflow/builder',
      color: 'bg-green-50 text-green-600'
    },
    {
      id: 'rules',
      title: 'Approval Rules Setup',
      description: 'Configure automated approval workflows and business rules.',
      icon: <CheckCircle size={24} />,
      path: '/workflow/rules',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      id: 'templates',
      title: 'Workflow Templates',
      description: 'Pre-built workflow templates for common business processes.',
      icon: <FileText size={24} />,
      path: '/workflow/templates',
      color: 'bg-orange-50 text-orange-600'
    },
    {
      id: 'reports',
      title: 'Workflow Reports',
      description: 'Comprehensive analytics and reporting for workflow performance.',
      icon: <BarChart3 size={24} />,
      path: '/workflow/reports',
      color: 'bg-blue-50 text-blue-600'
    }
  ];

  return (
    <div className="workflow-management" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white/20 p-4 rounded-2xl">
                <Workflow size={48} />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4">Workflow Management</h1>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              Manage and monitor all your business workflows in one centralized location.
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Workflow Tools</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive tools for creating, managing, and monitoring workflows
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workflowFeatures.map((feature) => (
              <Link
                key={feature.id}
                to={feature.path}
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-purple-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg ${feature.color}`}>
                    {feature.icon}
                  </div>
                  <ArrowRight 
                    size={20} 
                    className="text-gray-400 group-hover:text-purple-600 transition-colors" 
                  />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Start Building Workflows
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Create intelligent workflows that automate your business processes and drive efficiency.
          </p>
          <Link
            to="/workflow/builder"
            className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Target size={20} className="mr-2" />
            Build Workflow
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WorkflowManagement;
